import React from 'react';
import styled from '@emotion/styled';
import CIMButton from './CIMButton';
import { HERO_ZINDEX, CIM_WIDTH } from '../constants';
import { imageLoader } from '../../../../utils';
import Image from 'next/image';
import {
  colors, typography, size, breakpoints, FlexBox,
} from '@argo/principles';
import { Link, Heading, Box } from '@argo/ui-elements';
import { shopPrivateSellerCarsUrl, sellMyCarUrl, viewMyDashboardUrl } from '../../../constants/config';
import { sendClick } from 'reaxl-analytics';

const HeroCTAContainer = styled(Box)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${HERO_ZINDEX.CIM};
  width: 100%;
  min-width: 310px;
  background-color: ${colors.blue.marineBlue};
  border-radius: 5;

  @media (min-width: ${breakpoints.sm}px) {
    padding: ${size.lg}px ${size.sm}px;
  }

  @media (min-width: ${breakpoints.md}px) {
    padding: ${size.sm}px;
    width: calc(100% - (${2 * size.md}px));
    max-width: ${CIM_WIDTH.MD}px;
    margin-top: ${size.micro}px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${size.sm}px;
    left: 0;
    margin-top: ${size.xxxl}px;
    margin-bottom: ${size.xxxl}px;
    margin-left: ${size.sm}px;
    max-width: ${CIM_WIDTH.LG}px;
    transform: translateX(0);
  }

  @media (min-width: ${breakpoints.xl + 1}px) {
    margin-left: ${size.md}px;
    max-width: ${CIM_WIDTH.XL}px;
    padding: ${size.md}px;
  }

  @media (min-width: ${breakpoints.max}px) {
    left: calc((100% - ${size.contentMaxWidth}px) / 2);
  }

  @media (min-width: 280px) and (max-width: ${breakpoints.lg - 1}px) {
    padding: ${size.lg}px ${size.sm}px
  }
`;

const StyledHeading = styled(Heading)`
  padding-top: 10px;
  padding-bottom: ${size.sm}px;

  :first-of-type {
    color: ${colors.primary.white};
    @media (min-width: ${breakpoints.xs}px) and (max-width: ${breakpoints.md}px) {
      max-width: ${size.yuge * 3}px;
      margin-right: 5%;
      padding-top: 14px;
      font-size: 24px;
    }
    @media (min-width: ${breakpoints.md + 1}px) {
      margin-right: 0%;
      font-size: 24px;
    }
    @media (min-width: ${breakpoints.lg - 1}px) {
      margin-right: 0%;
      font-size: 28px;
      line-height: 36px;
    }
    @media (min-width: ${breakpoints.xl + 1}px) {
      margin-right: 10%;
      font-size: 36px;
    }
  }
`;

const SubHeading = styled.p`
  ${typography.types.body}
  color: ${colors.primary.white};
  font-weight: normal;
  margin-top: ${size.sm}px;

  @media (max-width: ${breakpoints.xs}) {
    margin-top: ${size.sm}px;
  }
  @media (min-width: ${breakpoints.xs + 1}px) and (max-width: ${breakpoints.md}px) {
    margin-top: ${size.sm}px;
  }
  @media (min-width: ${breakpoints.md + 1}px) and (max-width: ${breakpoints.lg}px) {
    margin-top: ${size.sm}px;
  }
  @media (min-width: ${breakpoints.lg + 1}px) and (max-width: ${breakpoints.xl}px) {
    margin-top: ${size.sm}px;
  }
  @media (min-width: ${breakpoints.xl + 1}px) {
    margin-top: ${size.sm}px;
  }
`;

const FlexBoxWrapper = styled(FlexBox)`
  @media (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.xl + 1}px) {
    flex-wrap: wrap;
    a:last-of-type {
      margin-top: ${size.xs}px;
    }
  }

  @media (min-width: ${breakpoints.xl + 2}px) {
    flex-wrap: wrap;
  }

  a {
    > span {
      min-height: 100%;
    }
    @media (min-width: ${breakpoints.lg}px) {
      span {
        font-size: 18px;
      }
    }
  }
`;

const CIMButtonStyle = styled(CIMButton)`
  height: 48px;
`;

const LinkStyle = styled(Link)`
  text-decoration: underline;
  color: white;
`;

function CIMContainer(props) {
  const onShopPrivateSellerClick = (event) => {
    sendClick('shopPrivateSellerClick', event);
  };

  const onSellMyCarClick = (event) => {
    sendClick('sellMyCarClick', event);
  };

  const onViewMyDashboardClick = (event) => {
    sendClick('viewMyDashboardClick', event);
  };

  return (
    <HeroCTAContainer {...props}>
      <Image
        loader={imageLoader}
        src="https://www.autotrader.com/psx-static/main/psx-buy-kbb/PSELogoshieldwhite.webp"
        alt="PSELogoshieldwhite"
        width={202}
        height={39}
      />
      <StyledHeading headingName="SectionHeading" enableTitleCase={false}>
        Shop like a pro from the resource you can trust.
      </StyledHeading>
      <FlexBoxWrapper
        flexWrap="wrap"
        default={['2', '2', '0 0 100%']}
        lg={['1 0 100%', '1 0 100%']}
        horizontalSpacing="xs"
      >
        <CIMButtonStyle
          id="shopPrivateSellerCarsButton"
          onClick={(event) => onShopPrivateSellerClick(event)}
          text="Shop Private Seller Cars"
          href={shopPrivateSellerCarsUrl}
          gaEventResult=""
          data-lean-auto=""
          data-analytics=""
        />
        <CIMButtonStyle
          id="sellMyCarButton"
          text="Sell My Car"
          onClick={(event) => onSellMyCarClick(event)}
          href={sellMyCarUrl}
          gaEventResult=""
          data-lean-auto=""
          data-analytics=""
          buttonType="secondary"
          target="_blank"
          rel="nofollow"
        />
      </FlexBoxWrapper>
      <SubHeading>
        Already Buying or Selling on KBB.com?&nbsp;
        <LinkStyle
          onClick={(event) => onViewMyDashboardClick(event)}
          href={viewMyDashboardUrl}
          rel="nofollow"
          target="_blank"
        >
          View My Dashboard
        </LinkStyle>
      </SubHeading>
    </HeroCTAContainer>
  );
}

export default CIMContainer;
